import { type RemixLinkProps } from '@remix-run/react/dist/components';
import { Link } from '~/remix';

type CommonProps = {
  size: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant: 'primary' | 'secondary' | 'tertiary' | 'danger';
  children: React.ReactNode;
  disabled?: boolean;
};
type LinkProps = RemixLinkProps &
  CommonProps & {
    type: 'link';
  };

type ButtonProps = JSX.IntrinsicElements['button'] & CommonProps;

export default function Button(props: ButtonProps | LinkProps) {
  const { type, variant, size, disabled, children } = props;
  const commonClasses =
    'rounded-full font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 inline-flex items-center gap-2';
  const sizeClasses = {
    xs: 'px-2.5 py-1 text-xs hover:shadow-sm',
    sm: 'px-2.5 py-1 text-sm hover:shadow-sm',
    md: 'px-4 py-1.5 text-sm hover:shadow-md',
    lg: 'px-5 py-2 text-md hover:shadow-md',
    xl: 'px-8 py-2.5 text-lg hover:shadow-lg',
  };
  const variantClasses = {
    primary:
      'bg-amber-600 hover:shadow-amber-700 text-white hover:bg-amber-600/80 focus-visible:outline-amber-600',
    secondary:
      'bg-ssm-purple hover:shadow-ssm-purple/50 text-white hover:bg-ssm-purple/80 focus-visible:outline-ssm-purple',
    tertiary:
      'bg-gray-50 hover:shadow-gray-300 text-gray-700 border-gray-300 border hover:bg-gray-100 ring-gray-300 focus-visible:outline-gray-300',
    danger:
      'bg-red-600 hover:shadow-red-700 text-white hover:bg-red-500 focus-visible:outline-red-600',
  };
  const disabledClasses =
    'bg-gray-600 hover:shadow-gray-700 text-white hover:bg-gray-600/80 focus-visible:outline-gray-600';

  var buttonClasses = disabled ? disabledClasses : variantClasses[variant];
  if (type === 'link') {
    return (
      <Link
        {...props}
        className={`${commonClasses} ${sizeClasses[size]} ${buttonClasses}`}
        onClick={(e) => {
          if (disabled) e.preventDefault();
        }}
      >
        {children}
      </Link>
    );
  }

  return (
    <button
      {...props}
      type={type}
      className={`${commonClasses} ${sizeClasses[size]} ${buttonClasses}`}
      onClick={props.onClick}
    >
      {children}
    </button>
  );
}
